import { capitalize, identity, kebabCase, maxBy, minBy, partition, range } from 'es-toolkit';

import { forceArray } from './forceArray';

/** This is like {@link keyBy} but returns a Map instead of an object dictionary. */
export function keyMapBy<K, T>(values: readonly T[], keyExtractor: (item: T) => K) {
  const result = new Map<K, T>();
  for (const value of values) {
    result.set(keyExtractor(value), value);
  }
  return result;
}

export const objectFromKeys = <K extends keyof any, V>(keys: ReadonlyArray<K>, getValueForKey: (key: K) => V) => {
  const result: { [K0 in K]?: V } = {};
  for (const key of keys) {
    result[key] = getValueForKey(key);
  }
  return result;
};

// For each element of the array, select a key for it using keyExtractor. Return a map from each distinct key produced
// to the elements that produced it.
export const getGroupingMap = <K, V>(
  values: ReadonlyArray<V>,
  keyExtractor: (v: V) => K,
): ReadonlyMap<K, ReadonlyArray<V>> => {
  const result = new Map<K, V[]>();
  for (const value of values) {
    const key = keyExtractor(value);
    const group = result.get(key);
    if (group) {
      group.push(value);
    } else {
      result.set(key, [value]);
    }
  }
  return result;
};

// For each element of the array, select any number of keys for it using keyExtractor. Return a map from each distinct
// key produced to the elements that produced it (with multiplicity if an element produces a key more than once).
export const getGroupingMapWithMultiKeySelector = <K, V>(
  values: ReadonlyArray<V>,
  keyExtractor: (v: V) => readonly K[],
): ReadonlyMap<K, ReadonlyArray<V>> => {
  const result = new Map<K, V[]>();
  for (const value of values) {
    for (const key of keyExtractor(value)) {
      const group = result.get(key);
      if (group) {
        group.push(value);
      } else {
        result.set(key, [value]);
      }
    }
  }
  return result;
};

export function humanCase(value: string) {
  return kebabCase(value).split('-').map(capitalize).join(' ');
}

/**
 * Provides an easy way to filter an array and narrow its type at the same time.
 *
 * @example
 * [1, 2, null, 4].filter(n => n != null); // [1, 2, 4]: (number | null)[]
 * [1, 2, null, 4].filter(notNullish);        // [1, 2, 4]: number[]
 */
export function notNullish<T>(item: T | null | undefined | void): item is T {
  return item != null;
}

/**
 * Provides an easy way to filter undefined values from an array and narrow its type at the same time.
 *
 * @example
 * [1, 2, undefined, null, 4].filter(n => n !== undefined); // [1, 2, null, 4]: (number | null | undefined)[]
 * [1, 2, undefined, null, 4].filter(notUndefined);         // [1, 2, null, 4]: (number | null)[]
 */
export function notUndefined<T>(item: T | undefined): item is T {
  return item !== undefined;
}

/**
 * Placed at the end of a switch statement to assert at compile time that all possible values were checked. Throws at
 *    runtime if anything unexpected manages to sneak through.
 *
 * @param switchValue The value used to select a case.
 *
 * @param errorMessage An error message to throw at runtime if an unexpected value is passed to the switch. If
 *   undefined, prints a default message and a string representation of the value.
 */
export function assertExhaustiveSwitch(switchValue: never, errorMessage?: string): never {
  throw new Error(errorMessage ?? `This code should be unreachable. Value: ${String(switchValue)}`);
}

/** Move the first `index` elements to the end of the array. */
export function cycle<T>(array: readonly T[], index: number): T[] {
  return array.slice(index).concat(array.slice(0, index));
}

/**
 * Creates an array of tuples by pairing each element with its predecessor, except for the first element which is only
 * returned as the predecessor of the second element.
 */
export function pairwise<T>([head, ...tail]: readonly T[]): [T, T][] {
  return tail.map((v, i) => [i === 0 ? head : tail[i - 1], tail[i]]);
}

/**
 * Creates an array of triples by pairing each element with its predecessor and successor, except for the first and last
 * elements.
 */
export function tripletWise<T>(array: readonly T[]): [T, T, T][] {
  const [head, next, ...tail] = array;
  const elem = (index: number) => (index === -2 ? head : index === -1 ? next : tail[index]);
  return tail.map((_v, i) => [elem(i - 2), elem(i - 1), elem(i)]);
}

/**
 * Creates an array of tuples where each element is paired with its successor and the last element is paired with the
 * first element. The returned array will be the same length as the passed array.
 */
export function circularPairwise<T>(array: readonly T[]): [T, T][] {
  return array.map((value, index) => [value, array[(index + 1) % array.length]]);
}

/**
 * Creates an array of tuples of 3 consecutive elements with the array interpreted as a loop. The returned array will be
 * the same length as the passed array.
 */
export function circularTripletwise<T>(array: readonly T[]): [T, T, T][] {
  function at(i: number) {
    if (i < 0) return array[i + array.length];
    if (i >= array.length) return array[i - array.length];
    return array[i];
  }
  return array.map((value, index) => [at(index - 1), value, at(index + 1)]);
}

export function groupAdjacentElementsCircularly<T>(
  elements: readonly T[],
  groupingPredicate: (item1: T, item2: T) => boolean,
): T[][] {
  const results: T[][] = [];
  const firstGroup: T[] = [];
  let currentGroup = firstGroup;
  for (const element of elements) {
    if (!currentGroup.length || groupingPredicate(currentGroup[currentGroup.length - 1], element)) {
      currentGroup.push(element);
    } else {
      if (currentGroup !== firstGroup) {
        results.push(currentGroup);
      }
      currentGroup = [element];
    }
  }
  if (!currentGroup.length) return results;
  if (firstGroup === currentGroup) {
    results.push(firstGroup);
  } else if (groupingPredicate(currentGroup[currentGroup.length - 1], firstGroup[0])) {
    results.push(currentGroup.concat(firstGroup));
  } else {
    results.push(currentGroup, firstGroup);
  }
  return results;
}

/**
 * Groups the source items based on which have "adjacent" keys, via the areAdjacent delegate and indirectly via the
 * keys of intermediate items. The areAdjacent delegate is assumed to be a symmetric condition; i.e.,
 * for all t1, t2: areAdjacent(t1, t2) === areAdjacent(t2, t1)
 */
export function groupTransitivelyByKey<T, U>(
  source: readonly T[],
  keySelector: (item: T) => U,
  areAdjacent: (item1: U, item2: U) => boolean,
): T[][] {
  const keys = source.map(keySelector);
  let indices = range(0, source.length);
  const groups: number[][] = [];

  while (indices.length !== 0) {
    // pick out the first index to make a new group
    const newGroup: number[] = [];
    const adjacentIndices: number[] = [indices.pop()!];
    // pop adjacent indices and add to group until none adjacent left
    while (adjacentIndices.length !== 0) {
      const adjacentIndex = adjacentIndices.shift()!;
      // find adjacent indices and store (popping off main list)
      const [newAdjacentIndices, notAdjacent] = partition(indices, (index) =>
        areAdjacent(keys[index], keys[adjacentIndex]),
      );
      indices = notAdjacent;
      adjacentIndices.push(...newAdjacentIndices);
      newGroup.push(adjacentIndex);
    }
    // push group
    groups.push(newGroup);
  }

  return groups.map((group) => group.map((index) => source[index]));
}

/**
 * Groups the source items based on which have "adjacent" keys, via the areAdjacent delegate. The areAdjacent delegate
 * is assumed to be a symmetric condition; i.e., for all t1, t2: areAdjacent(t1, t2) === areAdjacent(t2, t1)
 */
export function groupTransitively<T>(source: readonly T[], areAdjacent: (item1: T, item2: T) => boolean): T[][] {
  return groupTransitivelyByKey(source, identity, areAdjacent);
}

/**
 * Returns the most common item in the array. In case of a tie, the item that appears latest will be returned.
 *
 * Note that the given items must be comparable with `===`, and this function is not optimized for performance.
 */
export function mostCommon<T>(items: readonly T[]): T | undefined {
  return items.toSorted((a, b) => items.filter((v) => v === a).length - items.filter((v) => v === b).length).pop();
}

/**
 * Connect the given strings with commas and a conjunction (e.g., "and" or "or") the way they would be written if listed
 * in a sentence. Examples of formatted lists of varying lengths:
 *
 * @example
 * // returns 'humility'
 * formatAsNaturalLanguageList(['humility'], 'and')
 * @example
 * // returns 'fear and trembling'
 * formatAsNaturalLanguageList(['fear', 'trembling'], 'and')
 * @example
 * // returns 'faith, hope, and love'
 * formatAsNaturalLanguageList(['faith', 'hope', 'love'], 'and')
 */
export function formatAsNaturalLanguageList(items: readonly string[], conjunction: string) {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];

  const separator = items.some((item) => item.includes(', ')) ? ';' : ',';

  const allItemsButLast = items.slice(0, -1);
  const lastItem = items[items.length - 1];
  const joinedNames = allItemsButLast.join(`${separator} `);
  const possibleFinalSeparator = items.length > 2 ? separator : '';
  return `${joinedNames}${possibleFinalSeparator} ${conjunction} ${lastItem}`;
}

/**
 * Like `maxBy`, but returns all elements that have the maximum value according to the iteratee.
 */
export function maxByMultiple<T>(array: readonly T[], iteratee: (value: T) => number): T[] {
  const pairs = array.map((value) => [value, iteratee(value)] as const);

  const maxPair = maxBy(pairs, (pair) => pair[1]);
  if (maxPair === undefined) return [];

  const maxCriterion = maxPair[1];
  return pairs.filter(([_, criterion]) => criterion === maxCriterion).map((pair) => pair[0]);
}

/**
 * Like `minBy`, but returns all elements that have the minimum value according to the iteratee.
 */
export function minByMultiple<T>(array: readonly T[], iteratee: (value: T) => number): T[] {
  const pairs = array.map((value) => [value, iteratee(value)] as const);

  const minPair = minBy(pairs, (pair) => pair[1]);
  if (minPair === undefined) return [];

  const minCriterion = minPair[1];
  return pairs.filter(([_, criterion]) => criterion === minCriterion).map((pair) => pair[0]);
}

export function allPairs<T>(array: readonly T[]): (readonly [T, T])[] {
  return array.flatMap((element1, i) => array.slice(0, i).map((element2) => [element1, element2] as const));
}

export function throwIfNull<T>(nullable: T | null | undefined, message?: string): T {
  if (nullable == null) {
    throw new Error(message);
  }

  return nullable;
}

type SortBySelector<T> = (item: T) => number | string | Date;
type Order = 'asc' | 'desc';

export function sortBy<T>(array: readonly T[], selector: SortBySelector<T>, order?: Order): T[];
export function sortBy<T>(
  array: readonly T[],
  selectors: readonly [SortBySelector<T>, SortBySelector<T>],
  orders?: readonly [Order, Order],
): T[];
export function sortBy<T>(
  array: readonly T[],
  selectors: [SortBySelector<T>, SortBySelector<T>, SortBySelector<T>],
  orders?: readonly [Order, Order, Order],
): T[];
export function sortBy<T>(
  array: readonly T[],
  selectors: [SortBySelector<T>, SortBySelector<T>, SortBySelector<T>, SortBySelector<T>],
  orders?: readonly [Order, Order, Order, Order],
): T[];
export function sortBy<T>(
  array: readonly T[],
  selectors: [SortBySelector<T>, SortBySelector<T>, SortBySelector<T>, SortBySelector<T>, SortBySelector<T>],
  orders?: readonly [Order, Order, Order, Order, Order],
): T[];
export function sortBy<T>(
  array: readonly T[],
  selectors: ReadonlyArray<SortBySelector<T>> | SortBySelector<T>,
  orders: ReadonlyArray<Order> | Order | undefined,
): T[] {
  const selectorsArray = forceArray(selectors);
  const ordersArray = orders != null ? forceArray(orders) : undefined;

  if (ordersArray && selectorsArray.length !== ordersArray.length)
    throw new Error('The number of selectors did not match the number of orders.');

  return array.toSorted((a, b) => {
    for (let i = 0; i < selectorsArray.length; i++) {
      const selector = selectorsArray[i];
      const order = ordersArray?.[i] ?? 'asc';

      const valueA = selector(a);
      const valueB = selector(b);

      if (valueA < valueB) return order === 'asc' ? -1 : 1;
      if (valueA > valueB) return order === 'asc' ? 1 : -1;
    }

    return 0;
  });
}

export function omitBy<T>(obj: Record<string, T>, predicate: (value: T, key: string) => boolean): Record<string, T> {
  return Object.fromEntries(Object.entries(obj).filter(([key, value]) => !predicate(value, key)));
}

export function pickBy<T>(obj: Record<string, T>, predicate: (value: T, key: string) => boolean): Record<string, T> {
  return Object.fromEntries(Object.entries(obj).filter(([key, value]) => predicate(value, key)));
}

export function groupByWithoutNullishKeys<T>(
  array: readonly T[],
  selector: (item: T) => string | null | undefined,
): Record<string, T[]> {
  const result: Record<string, T[]> = {};

  for (const item of array) {
    const key = selector(item);
    if (key == null) continue;

    if (!Object.hasOwn(result, key)) {
      result[key] = [];
    }

    result[key].push(item);
  }

  return result;
}

export function min(array: readonly number[]): number | undefined {
  return array.length !== 0 ? Math.min(...array) : undefined;
}

export function max(array: readonly number[]): number | undefined {
  return array.length !== 0 ? Math.max(...array) : undefined;
}
